/**
 * ============================================================================
 * DATA MODELS — single source of truth for all dashboard types.
 * ============================================================================
 *
 * Data-layer contracts — every other track
 * (filters, tables, tabs) imports from here.
 *
 * IMPLEMENTATION NOTES:
 * - ExecutiveSummaryRow mirrors the grain of `public.quicksight_metrics_base`:
 *   one row per DCR × week × network × order category. The API returns this
 *   flat shape; the browser NEVER receives pre-pivoted data.
 * - DashboardFilters covers the full 21-filter list observed in the current
 *   QuickSight dashboard. Multi-selects are string arrays (empty = show all).
 *   Booleans are tri-state: true / false / null (null = show all).
 * - When the week window exceeds 12 weeks the API switches to
 *   'weekly-summary' granularity (see GetDataResponse in executiveSummaryApi)
 *   to keep payload < 1MB gzip and browser RAM < 50MB.
 */

/** The network groupings + 'all' for the summary landing tab. */
export type NetworkId =
  | 'all'
  | 'nova-network'
  | 'nova-general'
  | 'nova-expert'
  | 'alexa-network'
  | 'alexa-plus'
  | 'classic-alexa'
  | 'others';

/** One metric shown on a network card (KPI tile) on the Customer Domain Level Summary. */
export interface MetricDetail {
  name: string;
  isOnTrack: boolean;
  value?: number;
  target?: number;
  unit?: string;
}

/** A network KPI tile on the Customer Domain Level Summary (built by selectNetworkTiles). */
export interface NetworkCardData {
  id: string;
  name: string;
  description: string;
  type: string;
  size: string;
  onTimeDeliveryPct: number;
  metrics: MetricDetail[];
  callouts?: string;
}

/**
 * One flat row from POST /executive-summary/data.
 * Grain: DCR × week × network × order category.
 * All charts/tables/KPIs are derived from arrays of this via Arquero.
 */
export interface ExecutiveSummaryRow {
  // ---- dimensions (filterable) ----
  dcrId: string;
  week: string; // ISO week, e.g. "2026-W27"
  network: NetworkId;
  orderCategory: string; // 'Pilot' | 'Production'
  customer: string;
  customerType: string; // e.g. 'AGIDS'
  locale: string;
  channel: string;
  subChannel: string;
  domain: string;
  demandCategory: string;
  networkType: string;
  dcrStatus: string;
  deliveryType: string;
  uom: string; // Unit of Measurement
  metricGroup: string; // e.g. 'WBR Metrics Only'
  isInternalCustomer: boolean;
  isBackupDcr: boolean;
  isByocDcr: boolean;
  isOtsDcr: boolean;
  isBackfillDcr: boolean;
  isByotOfflineDcr: boolean;
  dcrCreatedDate: string; // ISO date

  // ---- metrics (aggregatable) ----
  requestedDemand: number;
  committedVolume: number;
  actualVolume: number;
  ingestedVolume: number;
  processedVolume: number;
  deliveredVolume: number;
  backlogVolume: number;
  tatP50Ms: number;
  tatP90Ms: number;
  tatP99Ms: number;
  lineagePassRate: number; // 0..1
  reworkCount: number;
  slaBreachCount: number;
  onTimeDeliveryPct: number; // 0..1
}

/**
 * Full filter state. Matches the 21 QuickSight filters, but the UI promotes
 * only the 90%-use-case filters (period, orderCategories, metricGroup,
 * customers) to the chip bar — the rest live in MoreFiltersPanel.
 *
 * CONVENTIONS:
 * - string[]        → multi-select; [] means "All" (no filter applied)
 * - boolean | null  → tri-state; null means "All"
 */
export interface DashboardFilters {
  // ---- promoted chips (visible by default) ----
  period: string; // 'WBR' | 'last-4-weeks' | 'last-13-weeks'
  orderCategories: string[];
  metricGroup: string; // 'wbr-only' | 'all'
  customers: string[];

  // ---- MoreFiltersPanel (the +14) ----
  onTimeDeliveryThresholdPct: number; // slider, default 95
  lastXHistoricalPeriods: number; // default 4
  nextXFuturePeriods: number; // default 4
  dcrCreatedDateRange: { from: string | null; to: string | null };
  uoms: string[];
  customerTypes: string[];
  locales: string[];
  channels: string[];
  subChannels: string[];
  domains: string[];
  demandCategories: string[];
  networkTypes: string[];
  dcrStatuses: string[];
  deliveryTypes: string[];
  dcrIds: string[];
  metricNames: string[];
  isInternalCustomer: boolean | null;
  isBackupDcr: boolean | null;
  isByocDcr: boolean | null;
  isOtsDcr: boolean | null;
  isBackfillDcr: boolean | null;
  isByotOfflineDcr: boolean | null;
}

export interface WeekWindow {
  from: string; // ISO week
  to: string; // ISO week
}

/** GET /executive-summary/refresh-status — the SOLE cache-freshness signal. */
export interface RefreshStatus {
  lastRefreshedAt: string; // ISO timestamp of last refresh_quicksight_metrics_base() run
}

// ============================================================================
// VIEW CONFIG TYPES — mirror the JSON schema in src/config/views/*.json
// Adding a tab or table section = editing JSON, not writing a page component.
// ============================================================================

export interface MetricTableSectionConfig {
  id: string;
  title: string; // e.g. "Nova Network Summary - On Time Delivery %"
  owner?: string; // alias shown under the title
  goal?: string; // e.g. "≥ 80%"
  metrics: string[]; // metric names → rows of the table
  /**
   * False = catalog-skeleton rule for this section (same as Launch TAT /
   * DCR Health): every configured row renders in every network section,
   * empty weeks show "–" — a row never disappears just because this
   * network has no data for it. Default (absent/true) keeps the
   * presence-based behavior.
   */
  requirePresence?: boolean;
}

export interface WbrViewTab {
  id: string;
  label: string;
  /** Maps to a component via TAB_COMPONENTS registry in ExecutiveSummaryPage. */
  type: 'table-sections' | 'deep-dive' | 'dive-deep-dcr' | 'dive-deep-so' | 'network-health' | 'glossary';
  sections?: MetricTableSectionConfig[];
}

export interface WbrView {
  viewId: string;
  displayName: string;
  description?: string;
  defaultWeekWindowSize: number; // weeks
  tabs: WbrViewTab[];
}

// ============================================================================
// GLOSSARY TYPES — for the Glossary tab (metric definitions + term glossary).
// Served by GET /executive-summary/glossary.
// ============================================================================

export interface MetricDefinition {
  order: number;
  metricName: string;
  reportingBasis: string;
  definition: string;
  example: string;
}

export interface GlossaryTerm {
  category: string;
  term: string;
  description: string;
}

export interface GlossaryResponse {
  metricDefinitions: MetricDefinition[];
  terms: GlossaryTerm[];
}

/** Saved filter preset — persisted to localStorage keyed by alias. */
export interface FilterPreset {
  name: string;
  filters: DashboardFilters;
  savedAt: string;
}

export const EMPTY_FILTERS: DashboardFilters = {
  period: 'WBR',
  orderCategories: [],
  metricGroup: 'wbr-only',
  customers: [],
  onTimeDeliveryThresholdPct: 95,
  lastXHistoricalPeriods: 4,
  nextXFuturePeriods: 4,
  dcrCreatedDateRange: { from: null, to: null },
  uoms: [],
  customerTypes: [],
  locales: [],
  channels: [],
  subChannels: [],
  domains: [],
  demandCategories: [],
  networkTypes: [],
  dcrStatuses: [],
  deliveryTypes: [],
  dcrIds: [],
  metricNames: [],
  isInternalCustomer: null,
  isBackupDcr: null,
  isByocDcr: null,
  isOtsDcr: null,
  isBackfillDcr: null,
  isByotOfflineDcr: null,
};
